// External libraries
import React, { useEffect } from 'react';
import { BodyText } from '@paypalcorp/pp-react-text-weasley-v7';
import base64 from 'base64-paypal';
import { useStoreState } from '@/store';
import { stringify } from 'qs';
import { MULTI_PRODUCT_SUPPORTED } from '@/lib/constants';

// Interal modules
import { features } from '@/lib/features';
import { getLocale, getToken } from '@/lib/globals';
import { getContent } from '@/lib/content';
import { isIncontext } from '@/lib/utils/incontext';
import { getParams } from '@/lib/utils/params';
import { buildUrl } from '@/lib/utils/url';
import {
  logOnClickForcedSignupBNPL,
  logOnClickOptionalGuestBNPL,
  logWhenDisplayForcedSignupBNPL,
  logWhenDisplayOptionalGuestBNPL,
} from './fpti';
import { Link } from '@/components/Common';
import {
  atomics,
  atomicsCi,
  atomicsDomain,
  atomicsFlows,
  atomicsStatus,
  atomicsTasks,
  atomicsViewNames,
} from '@/lib/atomics';

const BASE_CANCEL_URI = '/checkoutweb/cancelToMerchant';

const getLinkContent = () => {
  if (features.isActive('isForcedSignUp')) {
    return getContent('bnpl.textLink.guest');
  }

  return getContent('bnpl.textLink.signup');
};

const clickHandler = () => {
  /** Added atomic leave flow event to track metrices */
  atomics.leaveFlow(atomicsViewNames.ryp, atomicsStatus.ok, {
    domain: atomicsDomain,
    task: atomicsTasks.rypLoaded,
  });
  if (features.isActive('isForcedSignUp')) {
    logOnClickForcedSignupBNPL();
  } else {
    logOnClickOptionalGuestBNPL();
  }
};

export const PayLaterTextLink = (): JSX.Element => {
  const { cancelUrl } = useStoreState(state => state.api);
  const isForcedSignUp = features.isActive('isForcedSignUp');
  const { country, locale } = getLocale();
  const isMultiProductSupported = MULTI_PRODUCT_SUPPORTED.includes(country);

  useEffect(() => {
    if (isForcedSignUp) {
      logWhenDisplayForcedSignupBNPL();
    } else {
      logWhenDisplayOptionalGuestBNPL();
    }
  }, []);

  const hasCancelURL = cancelUrl !== '';
  const returnParams = {
    ...getParams(),
    dumbledoreEligible: true,
    fromSignup: 'true',
    fundingSource: 'paylater',
    multiProductSupported: isMultiProductSupported,
  };

  if ('country.x' in returnParams) {
    delete returnParams['country.x'];
  }

  if ('locale.x' in returnParams) {
    delete returnParams['locale.x'];
  }

  const flowObj = {
    cancelUrl: hasCancelURL
      ? `${BASE_CANCEL_URI}?token=${getToken()}&isInContext=${isIncontext()}`
      : '/', // using '/' to indicate no cancel url case
    returnUrl: `/checkoutweb?${stringify(returnParams, {
      arrayFormat: 'repeat',
    })}`,
  };

  const encodedFlowObj = base64.encode(
    JSON.stringify(flowObj),
    base64.CharacterSet.PPBASE64,
    false,
  );

  const url = buildUrl('/welcome/flow/gpl', {
    additionalParams: {
      'country.x': country,
      flow: encodedFlowObj,
      fltk: getToken(),
      'locale.x': locale,
      product_flow: 'PAY_LATER',
    },
    withCurrentParams: false,
  });

  return (
    <BodyText as="p" strong>
      <Link
        href={url}
        testId="bnpl-link"
        onClick={clickHandler}
        lineHeight="24px"
        margin={'0 !important'}
        text={getLinkContent()}
        atomicEventsAttributes={{
          domain: atomicsDomain,
          flow: atomicsFlows.guestAndSignup,
          intent: atomicsCi.clickGuestSelectPayLaterOptionsLink,
          task: atomicsTasks.rypLoaded,
          viewName: atomicsViewNames.ryp,
        }}
      />
    </BodyText>
  );
};
